import { useMemo } from 'react';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { ScheduleTypes } from '@frontend/api-schedule';
import type { CalendarColumnGroup, CalendarEvent, ProviderCalendarColumn } from '@frontend/calendar-grid';
import { getFullName } from '@frontend/name';
import { useAppScopeStore } from '@frontend/scope';
import { useCalendarLocationIds } from '../stores/calendar-location.store';
import { useCalendarStore } from '../stores/calendar.store';
import { getLocalTimezone, getTimeDiff } from '../utils/date-helpers';
import { CalendarTimeLineViewV2Props } from '../views/Calendar/CalendarTimeLineViewV2';
import { useGetCalendarV2QueriesData } from './use-get-calendar-v2-queries-data';

dayjs.extend(utc);
dayjs.extend(timezone);

const checkIsUnassignedProvider = (
  appointment: ReturnType<typeof useGetCalendarV2QueriesData>['appointments'][number],
  providerId: string,
  providerDisplayName: string
): boolean => {
  return (
    !appointment.practitionerName &&
    !!appointment.providerIds?.length &&
    appointment?.providerIds[0] === '' &&
    providerId === `unassigned-${appointment.locationId}` &&
    providerDisplayName === 'unassigned'
  );
};

const formatForSorting = (str: string) => str.replace(/[^a-zA-Z0-9\s]/g, ' ').toLowerCase();
const getProviderName = (provider: ScheduleTypes.SchemaProvider) => {
  return getFullName(provider) || provider.publicDisplayName?.trim() || provider.resourceName || '';
};

const sortProvidersByName = (providers: ScheduleTypes.SchemaProvider[]): ScheduleTypes.SchemaProvider[] => {
  // Separate unassigned providers from regular providers
  const regularProviders = providers.filter((provider) => provider.publicDisplayName !== 'unassigned');
  const unassignedProviders = providers.filter((provider) => provider.publicDisplayName === 'unassigned');

  // Sort only the regular providers alphabetically, then append unassigned providers at the end
  const sortedRegularProviders = regularProviders.sort((a, b) => {
    // Old Name used for sorting: a.publicDisplayName || a.lastName || a.firstName || a.resourceName || ''
    const aName = formatForSorting(getProviderName(a));
    const bName = formatForSorting(getProviderName(b));
    return aName.localeCompare(bName);
  });

  return [...sortedRegularProviders, ...unassignedProviders];
};

type CalendarGridV2Data = {
  headerType: 'provider';
  columns: ProviderCalendarColumn[];
  events: CalendarEvent[];
  columnGroups: CalendarColumnGroup[];
};

export const useCalendarGridV2Data = ({
  filteredAppointments,
  filteredProviderList,
  providerExceptions,
  providersOfficeHours,
  officeHours,
  officeExceptions,
}: CalendarTimeLineViewV2Props): CalendarGridV2Data => {
  const { calendarDate } = useCalendarStore(['calendarDate']);
  const calendarDateDay = dayjs(calendarDate)
    .format('dddd')
    .toLowerCase() as keyof ScheduleTypes.OfficeHoursWeekSchedule;
  const { calendarLocationIds } = useCalendarLocationIds();
  const { getScopeName } = useAppScopeStore();

  const sortedProviders = useMemo(() => sortProvidersByName(filteredProviderList), [filteredProviderList]);

  return useMemo(() => {
    const columns: ProviderCalendarColumn[] = [];
    const events: CalendarEvent[] = [];
    const columnGroups: CalendarColumnGroup[] = [];

    let groupOffset = 0;

    for (const locationId of calendarLocationIds) {
      // const locationTimezone = getLocationTimeZone(locationId);
      const locationProviders = sortedProviders.filter((p) => p.locationId === locationId);

      const columnIds: string[] = [];

      for (const provider of locationProviders) {
        const columnId = provider.id ?? '';
        if (!columnId) continue;
        const columnEventIds: string[] = [];

        //#region Office Breaks & Exceptions

        // Office Breaks
        const locationOfficeHours = officeHours?.find((office) => office.locationId === locationId)?.officeHours;
        const selectedDayOfficeHours = locationOfficeHours?.[calendarDateDay];
        selectedDayOfficeHours?.forEach((officeHour) => {
          const breaks = officeHour.breaks ?? [];
          breaks.forEach((breakItem) => {
            if (breakItem.startTime && breakItem.endTime) {
              const eventId = `break-${calendarDateDay}-${breakItem.startTime}-${breakItem.endTime}`;
              events.push({
                id: eventId,
                startTime: breakItem.startTime,
                duration: getTimeDiff(breakItem.startTime, breakItem.endTime, { format: 'HH:mm' }),
                eventType: 'exception',
                // name: 'Break'
              });
              columnEventIds.push(eventId);
            }
          });
        });

        // Office Exceptions
        const selectedLocationOfficeExceptions = officeExceptions.filter(
          (ex) => ex.locationId === locationId && !ex.isAvailable && !!ex.id
        );
        selectedLocationOfficeExceptions.forEach((ex) => {
          events.push({
            id: ex.id ?? '',
            eventType: 'exception',
            startTime: dayjs(ex.startDateTime).format('HH:mm'),
            duration: getTimeDiff(dayjs(ex.startDateTime).format('HH:mm'), dayjs(ex.endDateTime).format('HH:mm'), {
              format: 'HH:mm',
            }),
            // name: ex.name?.trim() || 'Out of Office',
          });
          columnEventIds.push(ex.id ?? '');
        });

        //#endregion

        //#region Provider Breaks & Exceptions

        // Provider Breaks
        const providerOfficeHours = providersOfficeHours?.filter((p) => p.providerId === columnId);
        providerOfficeHours.forEach((providerOfficeHour) => {
          const breaks = providerOfficeHour.breaks ?? [];
          breaks.forEach((breakItem) => {
            if (breakItem.startTime && breakItem.endTime) {
              const eventId = `break-${providerOfficeHour.dayOfWeek}-${breakItem.startTime}-${breakItem.endTime}`;
              events.push({
                id: eventId,
                startTime: breakItem.startTime,
                duration: getTimeDiff(breakItem.startTime, breakItem.endTime, { format: 'HH:mm' }),
                eventType: 'exception',
                // name: 'Break'
              });
              columnEventIds.push(eventId);
            }
          });
        });

        // Provider Exceptions
        const selectedProviderExceptions = providerExceptions.filter(
          (ex) => ex.providerId === columnId && !ex.isAvailable && !!ex.id
        );
        selectedProviderExceptions.forEach((ex) => {
          events.push({
            id: ex.id ?? '',
            eventType: 'exception',
            startTime: dayjs(ex.startDateTime).format('HH:mm'),
            duration: getTimeDiff(dayjs(ex.startDateTime).format('HH:mm'), dayjs(ex.endDateTime).format('HH:mm'), {
              format: 'HH:mm',
            }),
            // name: ex.name?.trim() || 'Unavailable',
          });
          columnEventIds.push(ex.id ?? '');
        });

        //#endregion

        const providerDisplayName = provider.publicDisplayName?.trim() ?? '';
        const providerName = getProviderName(provider);
        const providerAppointments = filteredAppointments.filter((appt) => {
          const isUnassignedProvider = checkIsUnassignedProvider(appt, columnId, providerDisplayName);
          if (isUnassignedProvider) {
            return columnId === `unassigned-${locationId}` && providerDisplayName === 'unassigned';
          } else {
            return (
              (!!appt.providerIds?.length || providerName) &&
              appt.locationId === locationId &&
              appt.providerIds?.some((id) => {
                const eventPractitionerName = appt.practitionerName?.trim();
                if (id && columnId) {
                  return id === columnId;
                } else if (providerName && eventPractitionerName === providerName.trim()) {
                  return true;
                } else if (providerDisplayName && eventPractitionerName === providerDisplayName.trim()) {
                  return true;
                }
                return false;
              })
            );
          }
        });

        providerAppointments.forEach((appt) => {
          // TODO: ref old map function here: libs/features/schedule/src/hooks/use-get-appointments-events.ts
          events.push({
            id: appt.id ?? '',
            startTime: dayjs(appt.start).format('HH:mm'),
            duration: appt.duration ?? 0,
            eventType: 'appointment',
          });
          columnEventIds.push(appt.id ?? '');
        });

        columns.push({
          id: columnId,
          name: providerName,
          providerImage: provider.publicDisplayImage || '',
          eventIds: columnEventIds,
        });

        columnIds.push(columnId);
      }

      columnGroups.push({
        id: locationId,
        name: calendarLocationIds.length > 1 ? getScopeName(locationId) : '',
        columnIds,
        groupOffset,
        timezone: getLocalTimezone(),
      });

      groupOffset += columnIds.length;
    }

    return { headerType: 'provider' as const, columns, events, columnGroups };
  }, [
    calendarLocationIds,
    sortedProviders,
    getScopeName,
    officeHours,
    calendarDateDay,
    officeExceptions,
    providersOfficeHours,
    providerExceptions,
    filteredAppointments,
  ]);
};
